(function () {
  const STORAGE_KEY = 'ea_wishlist';

  /* ============ Storage layer ============ */

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveWishlist(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  function isInWishlist(id) {
    return getWishlist().some((item) => item.id === id);
  }

  function addToWishlist(product) {
    const items = getWishlist();
    if (items.some((item) => item.id === product.id)) return false;
    items.unshift(product);
    saveWishlist(items);
    return true;
  }

  function removeFromWishlist(id) {
    saveWishlist(getWishlist().filter((item) => item.id !== id));
  }

  // Returns true if ADDED, false if REMOVED.
  function toggleWishlist(product) {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
      return false;
    }
    addToWishlist(product);
    return true;
  }

  /* ============ Cart add (from drawer) ============ */

  function cartAddUrl() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart/add.js';
  }

  function addItemToCart(variantId, buttonEl) {
    if (!variantId || buttonEl.disabled) return;

    buttonEl.classList.add('is-loading');
    buttonEl.disabled = true;

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) throw new Error(data.description || 'Could not add to cart');

        buttonEl.classList.remove('is-loading');
        buttonEl.classList.add('is-added');

        // Flag that the wishlist drawer should reopen automatically after
        // the reload, so the person sees the drawer stay put and the
        // header cart count/cart drawer reflect the new item once they
        // close it — instead of the page reloading with everything shut.
        sessionStorage.setItem('ea_wishlist_reopen', 'true');

        setTimeout(() => {
          window.location.reload();
        }, 500);
      })
      .catch(() => {
        buttonEl.classList.remove('is-loading');
        buttonEl.disabled = false;
      });
  }

  /* ============ <wishlist-drawer> custom element ============
     Same pattern as Horizon's native <cart-drawer>: exposes .open()
     and .close() methods, owns its own render, and never loses its
     listeners even if surrounding sections re-render. */

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.itemsContainer = this.querySelector('[data-wishlist-items]');
      this.emptyState = this.querySelector('[data-wishlist-empty]');
      this.overlay = this.querySelector('[data-wishlist-overlay]');

      this.overlay?.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-wishlist-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.hasAttribute('data-open')) this.close();
      });

      this.render();
    }

    open() {
      this.setAttribute('data-open', 'true');
      document.body.classList.add('wishlist-drawer-open');
      this.render();
    }

    close() {
      this.removeAttribute('data-open');
      document.body.classList.remove('wishlist-drawer-open');
    }

    toggle() {
      if (this.hasAttribute('data-open')) this.close();
      else this.open();
    }

    render() {
      if (!this.itemsContainer) return;
      const items = getWishlist();

      if (items.length === 0) {
        this.itemsContainer.innerHTML = '';
        if (this.emptyState) this.emptyState.style.display = 'flex';
        return;
      }
      if (this.emptyState) this.emptyState.style.display = 'none';

      this.itemsContainer.innerHTML = items
        .map(
          (item) => `
        <div class="wishlist-item" data-wishlist-item-id="${item.id}">
          <a href="${item.url}" class="wishlist-item__image">
            <img src="${item.image}" alt="${item.title}" loading="lazy" width="80" height="106">
          </a>
          <div class="wishlist-item__content">
            <div class="wishlist-item__top-row">
              <a href="${item.url}" class="wishlist-item__title">${item.title}</a>
              <button
                type="button"
                class="wishlist-item__add-to-cart"
                data-wishlist-add-to-cart="${item.id}"
                data-variant-id="${item.variantId || ''}"
                aria-label="Add to cart"
                ${item.variantId ? '' : 'disabled'}
              >
                <svg class="icon-plus" viewBox="0 0 20 20" fill="none" width="16" height="16"><path d="M10 3V17M3 10H17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                <svg class="icon-check" viewBox="0 0 20 20" fill="none" width="16" height="16"><path d="M4 10.5L8 14.5L16 5.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <span class="wishlist-item__spinner"></span>
              </button>
            </div>
            ${item.variantTitle && item.variantTitle !== 'Default Title' ? `<div class="wishlist-item__variant">${item.variantTitle}</div>` : ''}
            <div class="wishlist-item__price">${item.price}</div>
            <button type="button" class="wishlist-item__remove" data-wishlist-remove="${item.id}">Remove</button>
          </div>
        </div>
      `
        )
        .join('');
    }
  }

  if (!customElements.get('wishlist-drawer')) {
    customElements.define('wishlist-drawer', WishlistDrawer);
  }

  function getDrawer() {
    return document.querySelector('wishlist-drawer');
  }

  /* ============ Header badge + product button states ============ */

  function updateCountBadges() {
    const count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';

      const wrapper = el.closest('.header-actions__wishlist');
      if (wrapper) {
        wrapper.classList.toggle('header-actions__wishlist--has-items', count > 0);
      }
    });
  }

  function updateProductButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const id = btn.getAttribute('data-product-id');
      const active = isInWishlist(id);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  document.addEventListener('wishlist:updated', () => {
    updateCountBadges();
    updateProductButtons();
    getDrawer()?.render();
  });

  /* ============ Global click delegation ============
     Delegated on document, not queried once on load — so it keeps
     working even after Shopify re-renders the header/product sections
     (theme editor, cart AJAX updates, etc). */

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-wishlist-trigger]');
    if (trigger) {
      e.preventDefault();
      getDrawer()?.open();
      return;
    }

    const removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      removeFromWishlist(removeBtn.getAttribute('data-wishlist-remove'));
      return;
    }

    const addToCartBtn = e.target.closest('[data-wishlist-add-to-cart]');
    if (addToCartBtn) {
      addItemToCart(addToCartBtn.getAttribute('data-variant-id'), addToCartBtn);
      return;
    }

    const toggleBtn = e.target.closest('[data-wishlist-toggle]');
    if (toggleBtn) {
      const wasAdded = toggleWishlist({
        id: toggleBtn.getAttribute('data-product-id'),
        variantId: toggleBtn.getAttribute('data-variant-id'),
        variantTitle: toggleBtn.getAttribute('data-variant-title'),
        title: toggleBtn.getAttribute('data-product-title'),
        image: toggleBtn.getAttribute('data-product-image'),
        price: toggleBtn.getAttribute('data-product-price'),
        url: toggleBtn.getAttribute('data-product-url')
      });

      const shouldAutoOpen = toggleBtn.getAttribute('data-auto-open-drawer') === 'true';
      if (wasAdded && shouldAutoOpen) {
        getDrawer()?.open();
      }
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateCountBadges();
    updateProductButtons();
    reopenDrawerIfFlagged();
  });

  // In case this script runs after DOMContentLoaded already fired
  // (e.g. loaded with defer late, or injected dynamically).
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    updateCountBadges();
    updateProductButtons();
    reopenDrawerIfFlagged();
  }

  function reopenDrawerIfFlagged() {
    if (sessionStorage.getItem('ea_wishlist_reopen') !== 'true') return;
    sessionStorage.removeItem('ea_wishlist_reopen');
    // Small delay so the wishlist-drawer custom element has definitely
    // connected and rendered before we tell it to open.
    setTimeout(() => {
      getDrawer()?.open();
    }, 50);
  }

  window.Wishlist = {
    get: getWishlist,
    has: isInWishlist,
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
    count: () => getWishlist().length,
    openDrawer: () => getDrawer()?.open(),
    closeDrawer: () => getDrawer()?.close()
  };
})();